import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { AreaChart, Area, ResponsiveContainer, YAxis } from "recharts";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { useAva } from "@/context/AvaContext";
import { getBrainState } from "@/lib/brainCache";
import {
  BrainCircuit, LandPlot, HeartPulse, MessagesSquare, Activity, Volume2, Sparkles, WifiOff,
  Database, History, Users, Network,
} from "lucide-react";

const NAV_CARDS = [
  { to: "/super-brain", label: "Super Brain", desc: "Hebbian learning core", icon: BrainCircuit, color: "#00e5ff" },
  { to: "/advisor", label: "Formation Advisor", desc: "Win the next match", icon: LandPlot, color: "#ccff00" },
  { to: "/health", label: "Player Fitness", desc: "Fatigue & injury risk", icon: HeartPulse, color: "#ff3b30" },
  { to: "/console", label: "OCTON Console", desc: "Talk to the organism", icon: MessagesSquare, color: "#ff7300" },
];

const Stat = ({ label, value, unit, color }) => (
  <div className="card-tac p-5">
    <p className="font-tac text-[10px] tracking-widest text-white/40">{label}</p>
    <p className="font-head mt-2 text-4xl font-black tracking-tighter" style={{ color }}>
      {value}<span className="text-lg text-white/40">{unit}</span>
    </p>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const { speak, speaking } = useAva();
  const [snap, setSnap] = useState(null);
  const [teams, setTeams] = useState([]);
  const [offline, setOffline] = useState(false);
  const [bank, setBank] = useState(null);

  useEffect(() => {
    api.get("/teams").then((r) => setTeams(r.data)).catch(() => {});
    const load = () => api.get("/brain/train/deep/status").then((r) => setBank(r.data)).catch(() => {});
    load();
    getBrainState().then((r) => { setSnap(r.data); setOffline(r.offline); }).catch(() => {});
    const iv = setInterval(() => { load(); getBrainState().then((r) => setSnap(r.data)).catch(() => {}); }, 5000);
    return () => clearInterval(iv);
  }, []);

  const greet = () =>
    speak(
      `Welcome back, coach ${user?.name?.split(" ")[0] || ""}. OCTON is online and synced with the AVA mothership. My neural core has processed ${snap?.episodes || 0} match episodes across the leagues. I am ready to advise your formation, player mix and squad fitness.`
    );

  const curve = (snap?.reward_curve || []).map((v, i) => ({ i, v }));

  return (
    <div className="px-6 pb-24 pt-20 sm:px-10 lg:px-16">
      {/* hero */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <p className="font-tac text-[11px] tracking-[0.35em] txt-cyan">COMMAND DECK</p>
        <h1 className="font-head mt-2 text-5xl font-black uppercase tracking-tighter sm:text-6xl">
          Living <span className="txt-cyan">Super-Brain</span>
        </h1>
        <p className="mt-3 max-w-2xl text-sm text-white/50">
          A breathing tactical organism linked to AVA. Hebbian plasticity, a 512-dim CNN and Bayesian
          belief propagation, trained on the world's toughest leagues.
        </p>
        <button
          data-testid="ava-greet-btn"
          onClick={greet}
          className="mt-5 inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/5 px-5 py-2.5 text-sm text-cyan-200 transition hover:bg-cyan-400/10"
        >
          <Volume2 size={16} className={speaking ? "animate-pulse" : ""} /> Hear OCTON speak
        </button>
      </motion.div>

      {/* knowledge bank live ticker */}
      {bank && (
        <Link to="/super-brain" data-testid="knowledge-ticker" data-octon-section="knowledge bank ticker clubs seasons absorbed" data-octon-label="Knowledge Bank"
          className="mb-6 block overflow-hidden rounded-2xl border border-white/8 bg-gradient-to-r from-[#0a0a0a] via-[#0d0f0d] to-[#0a0a0a]">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 px-5 py-4">
            <span className="flex items-center gap-2 font-tac text-[10px] tracking-widest text-white/50">
              <span className={`h-2 w-2 rounded-full ${bank.running ? "animate-pulse bg-[#ccff00]" : bank.status === "paused_quota" ? "bg-orange-400" : "bg-white/30"}`} />
              KNOWLEDGE BANK {bank.running ? "· ABSORBING LIVE" : bank.status === "paused_quota" ? "· DAILY API LIMIT REACHED" : bank.status === "complete" ? "· HISTORY ABSORBED" : "· STANDBY"}
            </span>
            {[
              [Users, `${bank.vitals?.teams_known ?? 0}`, "clubs absorbed", "#00e5ff"],
              [History, `${bank.seasons_done}/${bank.seasons_total}`, "seasons absorbed", "#ccff00"],
              [Database, (bank.matches_learned || 0).toLocaleString(), "matches studied", "#ff7300"],
              [Network, `${bank.vitals?.team_pairs ?? 0}`, "club×tactic beliefs", "#ff3b30"],
            ].map(([Icon, v, k, c], i) => (
              <span key={i} className="flex items-baseline gap-2">
                <Icon size={15} style={{ color: c }} />
                <span className="font-head text-2xl font-black tracking-tighter" style={{ color: c }}>{v}</span>
                <span className="text-[11px] text-white/40">{k}</span>
              </span>
            ))}
            <span className="ml-auto hidden text-[11px] sm:inline" style={{ color: bank.status === "paused_quota" ? "#ffb800" : "#67e8f9" }}>
              {bank.running ? "OCTON is studying the history of the game →"
                : bank.status === "paused_quota" ? `Daily API limit reached (${(bank.quota?.current || 7500)}/${(bank.quota?.limit || 7500)}) — auto-resumes on reset →`
                : bank.status === "complete" ? "Full history absorbed →"
                : "Open Super Brain to keep learning →"}
            </span>
          </div>
          <div className="h-1 w-full bg-white/5">
            <div className="h-full bg-gradient-to-r from-cyan-400 to-[#ccff00] transition-all"
              style={{ width: `${bank.seasons_total ? Math.round((bank.seasons_done / bank.seasons_total) * 100) : 0}%` }} />
          </div>
        </Link>
      )}

      {/* stats */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="MATCH EPISODES" value={snap?.episodes ?? "—"} color="#00e5ff" />
        <Stat label="CONSOLIDATION" value={snap ? Math.round(snap.consolidation * 100) : "—"} unit="%" color="#ccff00" />
        <Stat label="SYNAPSE STRENGTH" value={snap ? snap.synapse_strength : "—"} color="#ff7300" />
        <Stat label="EMBEDDING DIM" value={snap?.embedding_dim ?? 512} color="#ff3b30" />
      </div>

      {/* learning signal + cards */}
      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <div className="card-tac relative overflow-hidden p-5 lg:col-span-2">
          <div className="mb-3 flex items-center gap-2">
            <Activity size={16} className="txt-cyan" />
            <span className="font-tac text-[10px] tracking-widest text-white/50">LEARNING SIGNAL · REAL-MATCH REWARD</span>
            {offline && (
              <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-orange-500/15 px-2 py-0.5 text-[9px] text-orange-300" data-testid="offline-badge">
                <WifiOff size={10} /> OFFLINE · CACHED
              </span>
            )}
          </div>
          <div className="h-[280px]" data-testid="learning-signal-chart" data-octon-section="learning signal chart brier calibration trend" data-octon-label="Learning Signal Chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={curve} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                <defs>
                  <linearGradient id="rewardFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ccff00" stopOpacity="0.35" />
                    <stop offset="100%" stopColor="#ccff00" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <YAxis domain={[-1, 1]} hide />
                <Area type="monotone" dataKey="v" stroke="#ccff00" strokeWidth={2} fill="url(#rewardFill)" dot={false} isAnimationActive />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 grid grid-cols-3 gap-3">
            {[
              ["REWARD POINTS", curve.length, "#ccff00"],
              ["AVG SYNAPSE", snap?.avg_weight ?? "—", "#ff7300"],
              ["PEAK SYNAPSE", snap?.synapse_strength ?? "—", "#00e5ff"],
            ].map(([k, v, c]) => (
              <div key={k} className="rounded-xl border border-white/8 px-3 py-2">
                <p className="font-mono text-lg font-bold" style={{ color: c }}>{v}</p>
                <p className="text-[9px] uppercase tracking-widest text-white/40">{k}</p>
              </div>
            ))}
          </div>
          <Link to="/super-brain" data-testid="open-super-brain" className="absolute right-4 top-4 inline-flex items-center gap-2 rounded-full bg-white/5 px-4 py-2 text-xs text-white/70 hover:text-cyan-300">
            <Sparkles size={14} /> Open the living brain
          </Link>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-1">
          {NAV_CARDS.map((c, i) => (
            <motion.div key={c.to} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.08 }}>
              <Link to={c.to} data-testid={`card-${c.label.toLowerCase().replace(/\s/g, "-")}`} className="card-tac flex items-center gap-4 p-4">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: `${c.color}18`, color: c.color }}>
                  <c.icon size={20} />
                </div>
                <div>
                  <p className="font-head text-lg font-bold tracking-tight">{c.label}</p>
                  <p className="text-xs text-white/40">{c.desc}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>

      {/* league marquee */}
      <div className="mt-6 card-tac overflow-hidden py-4">
        <p className="mb-3 px-5 font-tac text-[10px] tracking-widest text-white/40">TRAINING GROUNDS · {teams.length} CLUBS</p>
        <Marquee gradient gradientColor="#0a0a0a" gradientWidth={80} speed={40} pauseOnHover>
          {teams.map((t) => (
            <div key={t.id} className="mx-3 flex items-center gap-3 rounded-full border border-white/8 px-5 py-2">
              <img src={t.logo} alt="" className="h-6 w-6 object-contain" />
              <span className="text-sm text-white/80">{t.name}</span>
            </div>
          ))}
        </Marquee>
      </div>
    </div>
  );
}
